import { StaggeredAnimationItem, type StaggeredAnimationItemProps } from "#components/animation/StaggeredAnimationItem.tsx";
import { Div, type DivProps } from "@hopper-ui/components";
import { Children, cloneElement, isValidElement, type CSSProperties, type PropsWithChildren, type ReactElement, type ReactNode } from "react";
import "./StaggeredAnimation.css";

// Type based on the keyframe animations declared in the animations.css file
type AnimationType =
    "wai-reveal" |
    "wai-reveal-left-to-right" |
    "wai-reveal-bottom-to-top" |
    "wai-reveal-right-to-left" |
    "wai-hide-right-to-left" |
    "wai-up-and-down"
;

interface Props extends Partial<DivProps> {
    animation?: AnimationType;
    initialDelay?: number;
    delayIncrement?: number;
    /** Caps an item's delay, so a long list does not keep its last items hidden for seconds. */
    maxDelay?: number;
}

function isStaggeredAnimationItem(element: ReactNode): element is ReactElement<StaggeredAnimationItemProps> {
    return isValidElement(element) && element.type === StaggeredAnimationItem;
}

export const StaggeredAnimation = ({ children, animation = "wai-reveal", initialDelay = 0, delayIncrement = 60, maxDelay = Infinity, ...rest }: PropsWithChildren<Props>) => {
    // Transform children to add animation properties
    const staggeredChildren = Children
        .toArray(children)
        .filter(Boolean)
        .map((child, index) => {
            if (isStaggeredAnimationItem(child)) {
            // Calculate delay for this child
                const delay = Math.min(initialDelay + index * delayIncrement, maxDelay);

                // Clone the element with animation properties
                return cloneElement(child, {
                    style: {
                        ...child.props.style,
                        "--wai-staggered-animation-item-animation": animation,
                        "--wai-staggered-animation-item-delay": `${delay}ms`
                    } as CSSProperties
                });
            } else {
                console.warn("StaggeredAnimation only supports StaggeredAnimationItem as children. Other components will not be animated.");
            }

            return child;
        });

    return <Div {...rest}>{staggeredChildren}</Div>;
};
